import React, { type ComponentType, type ReactNode } from 'react';

import { Popup } from '@atlaskit/popup/popup';
import type { PopupComponentProps, PopupProps, TriggerProps } from '@atlaskit/popup/types';

import type {
	AsyncLoadOptions,
	FormatOptionLabel,
	OnChangeWithData,
	PaginationConfig,
	SingleOption,
} from '../common/types';
import { PaginatedPickerIntlProvider } from '../common/utils';

import PaginatedAsyncSelectView from './async-select';
import formatOptionLabelDefault from './format-option-label';
import { DialogInner } from './styled';

export interface PickerProps {
	ariaLabel?: string;
	fieldId: string;
	fieldInvalidLabel?: string;
	fieldLabel?: string;
	formatOptionLabel?: FormatOptionLabel;
	initiallyAddSelectedFirst?: boolean;
	isMulti?: boolean;
	isOpen: boolean;
	onChange: OnChangeWithData;
	onClearValues?: () => void;
	onClose: () => void;
	onLoadOptions: AsyncLoadOptions;
	onOpen: () => void;
	onTrigger: (a: TriggerProps) => ReactNode;
	pagination?: PaginationConfig;
	PopupComponent?: ComponentType<PopupComponentProps>;
	popupProps?: Partial<PopupProps>;
	showFooter?: boolean;
	value: readonly SingleOption[];
}

export const Picker = ({
	ariaLabel,
	isOpen,
	value,
	onClose,
	onClearValues,
	fieldId,
	fieldLabel,
	fieldInvalidLabel,
	onChange,
	formatOptionLabel = formatOptionLabelDefault,
	onLoadOptions,
	pagination,
	PopupComponent,
	onTrigger,
	initiallyAddSelectedFirst = true,
	isMulti = true,
	showFooter = true,
	popupProps,
}: PickerProps): React.JSX.Element => (
	<PaginatedPickerIntlProvider>
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={PopupComponent}
			shouldRenderToParent
			trigger={onTrigger}
			autoFocus={false}
			content={() => (
				<DialogInner>
					<PaginatedAsyncSelectView
						field={{
							isRemovable: false,
							label: fieldLabel || '',
							invalidLabel: fieldInvalidLabel,
							type: 'Filter',
							key: fieldId,
							defaultOptionsLabel: 'defaultOptionsLabel',
							defaultOptions: true,
							cacheOptions: false,
							inputValue: 'string',
							enableShowMore: true,
							pagination,
							loadOptions: onLoadOptions,
						}}
						onChange={onChange}
						value={value}
						formatOptionLabel={formatOptionLabel}
						onClear={onClearValues}
						initiallyAddSelectedFirst={initiallyAddSelectedFirst}
						isMulti={isMulti}
						showFooter={showFooter}
						ariaLabel={ariaLabel}
					/>
				</DialogInner>
			)}
			{...popupProps}
		/>
	</PaginatedPickerIntlProvider>
);
